import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiClient } from '../services/apiClient';
import firebaseConfigService from '../services/firebaseConfig';
import { collection, onSnapshot, where, query, orderBy, limit } from 'firebase/firestore';

// Resolved lazily: the tenant's database is only known after login, so a
// module-load capture would pin every tenant to (default) — TMT's data.
const getDb = () => firebaseConfigService.getServices().firestore;
let unsubscribeFromNotifications = null;

const sanitizeNotification = (id, data) => {
    const sanitized = { id, ...data };
    const toTimestamp = (val) => (val && typeof val.toMillis === 'function') ? val.toMillis() : val;
    if (sanitized.createdAt) sanitized.createdAt = toTimestamp(sanitized.createdAt);
    if (sanitized.taskDueDate) sanitized.taskDueDate = toTimestamp(sanitized.taskDueDate);
    if (sanitized.readAt) sanitized.readAt = toTimestamp(sanitized.readAt);
    return sanitized;
};

const initialState = {
    notifications: [],
    unreadCount: 0,
    loading: false,
    error: null,
};

// Async thunks
export const fetchUnreadNotifications = createAsyncThunk(
    'taskNotifications/fetchUnread',
    async () => {
        return await apiClient.get('/tasks/notifications/unread');
    }
);

export const checkNotifications = createAsyncThunk(
    'taskNotifications/check',
    async () => {
        return await apiClient.post('/tasks/notifications/check');
    }
);

export const markNotificationAsRead = createAsyncThunk(
    'taskNotifications/markAsRead',
    async (notificationId) => {
        await apiClient.put(`/tasks/notifications/${notificationId}/read`);
        return notificationId;
    }
);

export const markAllNotificationsAsRead = createAsyncThunk(
    'taskNotifications/markAllAsRead',
    async () => {
        await apiClient.put('/tasks/notifications/read-all');
    }
);

export const listenForNotifications = createAsyncThunk(
    'taskNotifications/listen',
    async ({ userId }, { dispatch, rejectWithValue }) => {
        if (!userId) return rejectWithValue('Missing userId');
        if (unsubscribeFromNotifications) unsubscribeFromNotifications();

        const q = query(
            collection(getDb(), 'task_notifications'),
            where('userId', '==', String(userId)),
            where('read', '==', false),
            orderBy('createdAt', 'desc'),
            limit(50)
        );

        unsubscribeFromNotifications = onSnapshot(q, (snapshot) => {
            const items = snapshot.docs.map(doc => sanitizeNotification(doc.id, doc.data()));
            dispatch(setNotifications(items));
        }, (err) => {
            console.error('Real-time notifications listener error:', err);
        });
        return true;
    }
);

export const stopListeningForNotifications = () => (dispatch) => {
    if (unsubscribeFromNotifications) {
        unsubscribeFromNotifications();
        unsubscribeFromNotifications = null;
    }
};

// Slice
const taskNotificationSlice = createSlice({
    name: 'taskNotifications',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        addNotification: (state, action) => {
            state.notifications.unshift(action.payload);
            state.unreadCount += 1;
        },
        setNotifications: (state, action) => {
            state.notifications = action.payload;
            state.unreadCount = action.payload.length;
        },
    },
    extraReducers: (builder) => {
        // Fetch unread notifications
        builder
            .addCase(fetchUnreadNotifications.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchUnreadNotifications.fulfilled, (state, action) => {
                state.loading = false;
                state.notifications = action.payload;
                state.unreadCount = action.payload.length;
            })
            .addCase(fetchUnreadNotifications.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch notifications';
            });

        // Check notifications
        builder
            .addCase(checkNotifications.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(checkNotifications.fulfilled, (state, action) => {
                state.loading = false;
                // Add new notifications to the list
                const newNotifications = action.payload.filter(
                    newNotif => !state.notifications.some(existing => existing.id === newNotif.id)
                );
                state.notifications = [...newNotifications, ...state.notifications];
                state.unreadCount = state.notifications.length;
            })
            .addCase(checkNotifications.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to check notifications';
            });

        // Mark as read
        builder
            .addCase(markNotificationAsRead.fulfilled, (state, action) => {
                const notificationId = action.payload;
                state.notifications = state.notifications.filter(n => n.id !== notificationId);
                state.unreadCount = Math.max(0, state.unreadCount - 1);
            });

        // Mark all as read
        builder
            .addCase(markAllNotificationsAsRead.fulfilled, (state) => {
                state.notifications = [];
                state.unreadCount = 0;
            });
    },
});

export const { clearError, addNotification, setNotifications } = taskNotificationSlice.actions;
export default taskNotificationSlice.reducer;
